import * as React from 'react';

import { App, Button, Col, Descriptions, Form, Input, Modal, Row } from 'antd';
import { deleteInvitation, resetInvitationState } from 'src/store/modules/invitationModule';
import { resetInterviewState, setPersonalInfo } from 'src/store/modules/interviewModule';
import { useAppDispatch, useAppSelector } from 'src/types/reduxHelpers';

import { InterviewComponent } from 'src/components/interviewComponents/types/interviewComponent';
import Item from 'antd/es/descriptions/Item';
import { appRoutes } from 'src/routes';
import { nameof } from 'ts-simple-nameof';
import { useApiErrorMessage } from 'src/hooks/useApiErrorMessage';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PersonalInfoProps {

}

interface PersonalInfoFormValues {
    name: string;
    facilityName: string;
    department: string;
    jobTitle: string;
}

export const PersonalInfo: InterviewComponent<PersonalInfoProps> = React.forwardRef((props, ref) => {
    const { t } = useTranslation();
    const { getErrorMessage } = useApiErrorMessage();

    const [form] = Form.useForm<PersonalInfoFormValues>();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const employee = useAppSelector((state) => state.invitation.employee);
    const invitationToken = useAppSelector((state) => state.invitation.token);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isLoading, setIsLoading] = React.useState(false);

    const { modal, notification } = App.useApp();

    const handleConfirmOk = async (): Promise<void> => {
        if (invitationToken) {
            setIsLoading(true);
            const deleteAction = await dispatch(deleteInvitation(invitationToken));
            setIsLoading(false);

            if (deleteInvitation.fulfilled.match(deleteAction)) {
                //actually success
                setIsModalVisible(false);

                modal.success({
                    closable: false,
                    title: t('Common.success'),
                    content: t('Components.InterviewComponents.PersonalInfo.wrong_employee_notification_text'),
                    onOk: () => {
                        dispatch(resetInterviewState());
                        dispatch(resetInvitationState());
                        navigate(appRoutes.home.path);
                    },
                });
            }
            else {
                // Error deleting
                notification.error({
                    message: t('Common.error'),
                    description: getErrorMessage(deleteAction.payload),
                });
            }

        }

    };

    const handleConfirmCancel = (): void => {
        setIsModalVisible(false);
    };
    React.useImperativeHandle(ref, () => ({
        submit: async () => {
            try {
                await form.validateFields();

                form.submit();

                return true;
            } catch (err) {
                return false;
            }
        },
    }));


    return (
        <Row
            align="middle"
            justify="center"
            gutter={[30, 20]}
        >
            <Col
                span={24}
            >
                <Form
                    layout="vertical"
                    form={form}
                    onFinish={(values) => {
                        dispatch(setPersonalInfo({
                            department: values.department,
                            jobTitle: values.jobTitle,
                            name: values.name,
                            facilityName: values.facilityName,
                        }));
                    }}
                >
                    <Form.Item
                        label={t('Components.InterviewComponents.PersonalInfo.name_label')}
                        initialValue={employee?.displayName}
                        name={nameof<PersonalInfoFormValues>(x => x.name)}
                        rules={[{ required: true }]}
                    >
                        <Input
                            readOnly={true}
                        />
                    </Form.Item>

                    <Form.Item
                        label={t('Components.InterviewComponents.PersonalInfo.facility_label')}
                        initialValue={employee?.facility?.facilityName}
                        name={nameof<PersonalInfoFormValues>(x => x.facilityName)}
                        rules={[{ required: true }]}
                    >
                        <Input
                            readOnly={true}
                        />
                    </Form.Item>

                    <Form.Item
                        label={t('Components.InterviewComponents.PersonalInfo.department_label')}
                        initialValue={employee?.departmentName}
                        name={nameof<PersonalInfoFormValues>(x => x.department)}
                        rules={[{ required: true }]}
                    >
                        <Input
                            readOnly={true}
                        />
                    </Form.Item>

                    <Form.Item
                        label={t('Components.InterviewComponents.PersonalInfo.job_title_label')}
                        initialValue={employee?.jobTitle}
                        name={nameof<PersonalInfoFormValues>(x => x.jobTitle)}
                    >
                        <Input
                            readOnly={true}
                        />
                    </Form.Item>
                </Form>
            </Col>

            <Col
                style={{ fontSize: '20px', fontWeight: 'lighter' }}
            >
                <Button
                    size="large"
                    danger={true}
                    type="primary"
                    onClick={() => setIsModalVisible(true)}
                >
                    {t('Components.InterviewComponents.PersonalInfo.wrong_employee_button_text')}
                </Button>
                <Modal
                    confirmLoading={isLoading}
                    centered={true}
                    open={isModalVisible}
                    onOk={() => {
                        handleConfirmOk();
                    }}
                    onCancel={handleConfirmCancel}
                    okText={t('Components.InterviewComponents.PersonalInfo.wrong_employee_modal_ok_text')}
                    cancelText={t('Components.InterviewComponents.PersonalInfo.wrong_employee_modal_cancel_text')}
                    okType={'danger'}
                >
                    {t('Components.InterviewComponents.PersonalInfo.wrong_employee_modal_text')}
                    <Descriptions
                        bordered={true}
                        column={{
                            xxl: 1,
                            xl: 1,
                            lg: 1,
                            md: 1,
                            sm: 1,
                            xs: 1,
                        }}
                    >
                        <Item label={t('Components.InterviewComponents.PersonalInfo.name_label')}>
                            {employee?.displayName}
                        </Item>
                        <Item label={t('Components.InterviewComponents.PersonalInfo.department_label')}>
                            {employee?.departmentName}
                        </Item>
                        <Item label={t('Components.InterviewComponents.PersonalInfo.job_title_label')}>
                            {employee?.jobTitle}
                        </Item>
                        <Item label={t('Components.InterviewComponents.PersonalInfo.facility_label')}>
                            {employee?.facility?.facilityName}
                        </Item>
                    </Descriptions>
                </Modal>
            </Col>
        </Row>
    );
});

PersonalInfo.displayName = 'PersonalInfo';
